// Admin > Orders "Send to packing" (2026-10-22).
//
// An unfulfilled order can skip picking and go straight to the dispatch
// desk: the BE allocates its stock, mints a temporary TMP-###### tote,
// binds it to the order and moves the order to 'dispatch'
// (POST /orders/:id/send-to-pack). The packer then scans or types that
// code on the Dispatch screen like any other tote. While the order is
// still waiting at the desk, reopening the modal shows the same code
// (GET /orders/:id/pack-tote) so it can be read off again. Print opens the
// Totes page's label modal on that one code (same printer, same per-device
// tote label size) so a label can go on the box (2026-10-23).
import { useEffect, useState } from "react";
import Barcode from "react-barcode";
import { Copy, PackageOpen, Check, Printer } from "lucide-react";
import { toast } from "react-toastify";
import { apiFetch } from "../../api/apiFetch";
import { useTranslation } from "../../i18n";
import SortingLabelPrintModal from "../SortingLabelPrintModal";
import { TOTE_LABEL_BASE, readToteLabelSize, saveToteLabelSize, toteLabel } from "../helpers/toteLabel";

interface Props {
    orderId: string;
    status: string;
    // A flagged order qualifies only while unpacked (Alex, 2026-10-27);
    // a packed one shipped short and takes Send extra box instead.
    packedAt?: string | null;
    readOnly: boolean;
    // The order moved to 'dispatch' — patch the page's copy of it.
    onSent: (orderId: string) => void;
}

const SendToPackPanel = ({ orderId, status, packedAt, readOnly, onSent }: Props) => {
    const { t } = useTranslation();
    const [toteId, setToteId] = useState<string | null>(null);
    const [confirming, setConfirming] = useState(false);
    const [sending, setSending] = useState(false);
    const [copied, setCopied] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [labelSize, setLabelSize] = useState(readToteLabelSize);

    useEffect(() => {
        setToteId(null);
        setConfirming(false);
        setCopied(false);
        if (status !== "dispatch") return;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}/pack-tote`);
                if (!res.ok) return;
                const body = await res.json();
                if (!cancelled) setToteId(body?.tote?.tote_id ?? null);
            } catch { /* non-fatal — the panel just stays hidden */ }
        })();
        return () => { cancelled = true; };
    }, [orderId, status]);

    const send = async () => {
        setSending(true);
        try {
            const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}/send-to-pack`, { method: "POST" });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
                toast.error(body?.message || t("send_to_pack_failed"));
                return;
            }
            setToteId(body.tote_id);
            setConfirming(false);
            onSent(orderId);
        } catch {
            toast.error(t("send_to_pack_failed"));
        } finally {
            setSending(false);
        }
    };

    const copy = async () => {
        if (!toteId) return;
        try {
            await navigator.clipboard.writeText(toteId);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error(t("send_to_pack_copy_failed"));
        }
    };

    const saveSize = async (w: number | null, h: number | null) => {
        if (!saveToteLabelSize(w, h)) return false;
        setLabelSize({ widthMm: w, heightMm: h });
        return true;
    };

    if (toteId) {
        return (
            <div className="send-to-pack-panel send-to-pack-panel--ready" data-testid="send-to-pack-ready">
                <div className="send-to-pack-text">
                    <strong>{t("send_to_pack_ready_title")}</strong>
                    <span>{t("send_to_pack_ready_hint")}</span>
                </div>
                <div className="send-to-pack-code">
                    <Barcode value={toteId} height={40} width={1.6} fontSize={14} margin={0} background="transparent" />
                </div>
                <button type="button" className="btn btn-secondary dispatch-modal-btn" onClick={copy}>
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? t("send_to_pack_copied") : t("send_to_pack_copy")}
                </button>
                <button type="button" className="btn btn-secondary dispatch-modal-btn" onClick={() => setPrinting(true)}>
                    <Printer size={14} />
                    {t("send_to_pack_print")}
                </button>
                {printing && (
                    <SortingLabelPrintModal
                        warehouse=""
                        title={t("totes_print_labels_title")}
                        hint={t("totes_print_label_hint", { tote: toteId })}
                        showRange={false}
                        styles={["tote"]}
                        defaultSize={TOTE_LABEL_BASE}
                        labels={[toteLabel({ tote_id: toteId })]}
                        widthMm={labelSize.widthMm}
                        heightMm={labelSize.heightMm}
                        onSaveSize={saveSize}
                        onClose={() => setPrinting(false)}
                    />
                )}
            </div>
        );
    }

    const flagged = status === "flagged" && !packedAt;
    if (status !== "unfulfilled" && !flagged) return null;

    return (
        <div className="send-to-pack-panel">
            <PackageOpen size={14} />
            <span className="send-to-pack-text">
                {confirming ? t(flagged ? "send_to_pack_confirm_flagged" : "send_to_pack_confirm") : t("send_to_pack_hint")}
            </span>
            <div className="send-to-pack-actions">
                {confirming ? (
                    <>
                        <button type="button" className="btn btn-ghost dispatch-modal-btn" disabled={sending} onClick={() => setConfirming(false)}>
                            {t("cancel")}
                        </button>
                        <button type="button" className="btn btn-primary dispatch-modal-btn" disabled={sending || readOnly} onClick={send}>
                            {sending ? t("send_to_pack_sending") : t("send_to_pack_button")}
                        </button>
                    </>
                ) : (
                    <button
                        type="button"
                        className="btn btn-secondary dispatch-modal-btn"
                        disabled={readOnly}
                        title={readOnly ? t("read_only_mode") : undefined}
                        onClick={() => setConfirming(true)}
                    >
                        {t("send_to_pack_button")}
                    </button>
                )}
            </div>
        </div>
    );
};

export default SendToPackPanel;
